/**
 * WebSmMissionStep — one step of a mission's chain (`M13 S2`), as the engine
 * models it: the step's text is its Text option, Done is the CLICK1 branch
 * to the next step, back is CLICK2. The chain is every step of the graph
 * with the same MissionID, in StepNo order, so this screen draws the whole
 * deck and knows where in it it stands. The player keeps one instance
 * mounted across the chain, so the stamp, the flight and the light moving
 * along the track play as the branch is taken.
 *
 * Options: MissionID / StepNo / Steps / Text / Hint.
 */
import { useEffect } from "react";
import { useFunnelStore, useStepperStore } from "@/store";
import { useAcceptedMission } from "@/features/funnel/api/use-mission-file";
import { useMissionFile } from "@/features/funnel/api/use-mission-file";
import { useMissionProgress } from "@/store/mission-progress";
import { tierOfLevel } from "./open-case";
import { chainFor, progressKey } from "./mission-steps";
import { MissionSteps } from "./steps-file";
import { streakFrom } from "../streak";
import type { StepTypeProps } from "./types";

export default function WebSmMissionStepType({
  getOpt,
  dispatch,
}: StepTypeProps) {
  const missionId = Number(getOpt("MissionID"));
  const stepNo = Number(getOpt("StepNo")) || 1;
  const graph = useStepperStore((s) => s.graph);
  const goTo = useStepperStore((s) => s.goTo);
  const chain = chainFor(graph, missionId);
  const { data: mission } = useAcceptedMission();
  const storedTier = useFunnelStore((s) => s.tier);
  const handler = useFunnelStore((s) => s.handler);
  const tier = storedTier ?? tierOfLevel(mission?.level) ?? "beginner";
  const { data: filed = [] } = useMissionFile();
  const streak = streakFrom(
    filed
      .map((f) => (f.filedAt ? new Date(f.filedAt).getTime() : NaN))
      .filter((t) => !Number.isNaN(t)),
  );
  // the steps before this one are done: the live screen's label reads it
  const current = stepNo - 1;
  const done = Array.from({ length: current }, (_, i) => i);
  const setDone = useMissionProgress((s) => s.setDone);
  useEffect(() => {
    setDone(progressKey(missionId), done);
    // once per step of the chain
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [missionId, stepNo]);

  if (!chain.length) return null;
  return (
    <MissionSteps
      tier={tier}
      mission={
        mission ?? {
          missionId,
          name: "Your mission",
          level: "",
          mission: "",
          provenTruths: [],
          doorNames: [],
        }
      }
      handler={handler}
      missionNo={filed.length + 1}
      streak={streak}
      steps={chain.map((s) => s.text)}
      done={done}
      onTick={() => dispatch("CLICK1")}
      onUntick={(i) => goTo(chain[i].stepName)}
      onStamp={() => dispatch("CLICK1")}
      onClose={() => goTo("Delivered")}
    />
  );
}
